import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient, describeApiError } from '../services/api';
import { SBPModal } from './SBPModal';
import type { NFTItem } from '../types';
import './ClaimRewardPage.css';

const PREVIEW_COUNT = 5;
const CATALOG_PER_PAGE = 51;

export const ClaimRewardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCatalogMode = searchParams.get('all') === '1';

  const [allNfts, setAllNfts] = useState<NFTItem[]>([]);
  const [displayedItems, setDisplayedItems] = useState<NFTItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [showSBPModal, setShowSBPModal] = useState(false);
  const [, setSbpCompleted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [selectedNftId, setSelectedNftId] = useState<string | null>(null);
  const [loadingNfts, setLoadingNfts] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nftStats, setNftStats] = useState<{ total: number; available: number } | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Load initial data
  useEffect(() => {
    const init = async () => {
      try {
        setLoadError(null);
        const [status, nfts] = await Promise.all([
          apiClient.getOnboardingStatus(),
          isCatalogMode
            ? apiClient.getRewardNFTs({ shuffle: true })
            : apiClient.getRewardNFTs({ limit: PREVIEW_COUNT, shuffle: true }),
        ]);

        // Fetch NFT stats separately so it doesn't block the page on error
        try {
          const statsData = await apiClient.getNftStats();
          setNftStats(statsData);
        } catch {
          console.error('Failed to fetch NFT stats');
        }

        setSbpCompleted(status.sbp_completed);
        setAllNfts(nfts);

        if (isCatalogMode) {
          setDisplayedItems(nfts.slice(0, CATALOG_PER_PAGE));
          setHasMore(nfts.length > CATALOG_PER_PAGE);
        } else {
          setDisplayedItems(nfts);
          setHasMore(nfts.length >= PREVIEW_COUNT);
        }
      } catch (error) {
        console.error('Failed to initialize:', error);
        setLoadError(describeApiError(error));
        setSbpCompleted(false);
        setAllNfts([]);
        setDisplayedItems([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingNfts(false);
      }
    };

    init();
  }, [isCatalogMode]);

  const loadMoreItems = useCallback(() => {
    const currentLength = displayedItems.length;
    const nextItems = allNfts.slice(0, currentLength + CATALOG_PER_PAGE);
    setDisplayedItems(nextItems);
    setHasMore(nextItems.length < allNfts.length);
  }, [displayedItems.length, allNfts]);

  // Infinite scroll in catalog mode
  useEffect(() => {
    if (!isCatalogMode || !hasMore || loadingNfts) return;
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreItems();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isCatalogMode, hasMore, loadingNfts, loadMoreItems]);

  const handleSelectNft = useCallback((nftId: string) => {
    setSelectedNftId(prev => prev === nftId ? null : nftId);
  }, []);

  const handleClaimReward = useCallback(async () => {
    if (!selectedNftId) {
      return;
    }

    try {
      const status = await apiClient.getOnboardingStatus();
      setSbpCompleted(status.sbp_completed);

      if (!status.sbp_completed) {
        setShowSBPModal(true);
      } else {
        setClaiming(true);
        try {
          const result = await apiClient.claimWhiteListReward(selectedNftId);
          if (result.success) {
            navigate('/white-list');
          } else {
            console.error('Failed to claim reward:', result.message);
            // Refresh list
            const updatedNfts = isCatalogMode
              ? await apiClient.getRewardNFTs({ shuffle: true })
              : await apiClient.getRewardNFTs({ limit: PREVIEW_COUNT, shuffle: true });
            setAllNfts(updatedNfts);
            if (isCatalogMode) {
              setDisplayedItems(updatedNfts.slice(0, Math.max(displayedItems.length, CATALOG_PER_PAGE)));
              setHasMore(updatedNfts.length > Math.max(displayedItems.length, CATALOG_PER_PAGE));
            } else {
              setDisplayedItems(updatedNfts);
              setHasMore(updatedNfts.length >= PREVIEW_COUNT);
            }
            setSelectedNftId(null);
          }
        } catch (claimError) {
          console.error('Error claiming reward:', claimError);
          try {
            const updatedNfts = isCatalogMode
              ? await apiClient.getRewardNFTs({ shuffle: true })
              : await apiClient.getRewardNFTs({ limit: PREVIEW_COUNT, shuffle: true });
            setAllNfts(updatedNfts);
            if (isCatalogMode) {
              setDisplayedItems(updatedNfts.slice(0, Math.max(displayedItems.length, CATALOG_PER_PAGE)));
              setHasMore(updatedNfts.length > Math.max(displayedItems.length, CATALOG_PER_PAGE));
            } else {
              setDisplayedItems(updatedNfts);
              setHasMore(updatedNfts.length >= PREVIEW_COUNT);
            }
            setSelectedNftId(null);
          } catch {
            // ignore refresh error
          }
        } finally {
          setClaiming(false);
        }
      }
    } catch (error) {
      console.error('Failed to check SBP status:', error);
      setShowSBPModal(true);
    }
  }, [navigate, selectedNftId, displayedItems.length, isCatalogMode]);

  // Close on outside press; capture + preventDefault avoids activating elements underneath (incl. ghost clicks after unmount)
  useEffect(() => {
    if (!showTooltip) return;
    const handlePointerDownOutside = (e: PointerEvent) => {
      if (tooltipRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      e.stopPropagation();
      setShowTooltip(false);
    };
    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () => document.removeEventListener('pointerdown', handlePointerDownOutside, true);
  }, [showTooltip]);

  if (loading) {
    return (
      <div className="claim-reward-page">
        <div className="claim-reward-loading">
          <div className="claim-reward-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-reward-page">
      <div className="claim-reward-header">
        <h1 className="claim-reward-title">Забрать ху#к</h1>
        <p className="claim-reward-subtitle">
          Выберите один NFT и заберите его абсолютно
        </p>
        <span className="claim-reward-free-badge">бесплатно</span>

        <div className="claim-tooltip-wrapper" ref={tooltipRef}>
          <button
            className="claim-tooltip-trigger"
            onClick={() => setShowTooltip(prev => !prev)}
          >
            <img src="/question.svg" alt="?" className="claim-tooltip-icon" />
            <span className="claim-tooltip-text">Какой ху#к мне нужен?</span>
          </button>
          {showTooltip && (
            <div className="claim-tooltip-bubble">
              <div className="claim-tooltip-arrow" />
              Мягкий, жёсткий, странный — любой NFT-ху#чек твой! Хватай тот, что отражает тебя или потом будешь жалеть!
            </div>
          )}
        </div>
      </div>

      {nftStats && (
        <div className="claim-reward-progress-section">
          <div className="progress-top-row">
            <span className="progress-top-label">Осталось:</span>
            <span className="progress-top-value">
              {nftStats.available} NFT <span className="progress-top-total">/ {nftStats.total} NFT</span>
            </span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-horizontal">
              <div 
                className="progress-bar-fill"
                style={{ width: `${nftStats.total > 0 ? ((nftStats.total - nftStats.available) / nftStats.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="nft-catalog">
        {loadingNfts ? (
          <div className="claim-reward-loading">
            <div className="claim-reward-spinner"></div>
          </div>
        ) : loadError ? (
          <div className="nft-empty-state nft-error-state">
            <p>{loadError}</p>
          </div>
        ) : allNfts.length === 0 ? (
          <div className="nft-empty-state">
            <p>NFT пока недоступны</p>
          </div>
        ) : (
          <>
            <div className={`nft-grid ${isCatalogMode ? 'nft-grid--catalog' : ''}`}>
              {displayedItems.map((nft) => (
                <div 
                  key={nft.id} 
                  className={`reward-nft-card ${selectedNftId === nft.id ? 'selected' : ''}`}
                  onClick={() => handleSelectNft(nft.id)}
                >
                  <div className="reward-nft-image-container">
                    <img 
                      src={nft.preview_image_url ?? nft.image_url} 
                      alt={nft.title}
                      className="reward-nft-image"
                      loading="lazy"
                    />
                    <div className={`reward-nft-checkbox ${selectedNftId === nft.id ? 'checked' : ''}`}>
                      {selectedNftId === nft.id && (
                        <div className="reward-nft-checkbox-dot" />
                      )}
                    </div>
                  </div>
                  <div className="reward-nft-info">
                    <h3 className="reward-nft-title">{nft.title}</h3>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && isCatalogMode && (
              <div ref={loadMoreRef} className="claim-reward-loading">
                <div className="claim-reward-spinner"></div>
              </div>
            )}
          </>
        )}
      </div>

      {allNfts.length > 0 && (
        <div className="claim-reward-bottom-bar">
          <button 
            className="claim-reward-btn button--accent"
            onClick={handleClaimReward}
            disabled={claiming || !selectedNftId}
          >
            {claiming ? 'Получение награды...' : 'Забрать ху#к'}
          </button>
          {hasMore && !isCatalogMode && (
            <button
              className="show-more-btn"
              onClick={() => navigate('/claim-reward?all=1')}
            >
              Смотреть больше
            </button>
          )}
        </div>
      )}

      <SBPModal 
        isOpen={showSBPModal} 
        onClose={() => setShowSBPModal(false)}
        pendingNftId={selectedNftId}
      />
    </div>
  );
};
